import { useId, useState } from "react";
import "./ThinkingIndicator.css";

interface Props {
  content: string;
  /** Row label. "Thinking" while the stream is live, "Thought process" once it has landed. */
  label?: string;
}

/**
 * The model's reasoning, as a collapsed disclosure with a text pane.
 *
 * INTERIM, flagged for promotion to the DS. The DS `Thinking` component covers
 * only the live "assistant is responding" dots, so the expandable reasoning
 * panel has no component yet. This is built entirely from `--fwfa-*` tokens and
 * mirrors `.fwfa-tool-call`'s construction: the WRAPPER owns the border, the
 * radius, and the overflow clip, so the row and the pane are two children of one
 * box and cannot come apart at the seam.
 *
 * It spends no yellow. Reasoning repeats several times in an ordinary thread,
 * same as a tool call, so it rides the same quiet mono machine voice rather than
 * pulling the eye with a gold spark on every turn.
 */
export function ThinkingIndicator({ content, label = "Thought process" }: Props) {
  const paneId = useId();
  const [expanded, setExpanded] = useState(false);
  const hasContent = content.length > 0;

  return (
    <div className="fv-reasoning" data-open={expanded ? "true" : undefined}>
      <button
        type="button"
        className="fv-reasoning-row"
        aria-expanded={hasContent ? expanded : undefined}
        aria-controls={hasContent ? paneId : undefined}
        disabled={!hasContent}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="fv-reasoning-mark">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3a4 4 0 0 0-4 4 3.5 3.5 0 0 0-1 6.9V17a4 4 0 0 0 8 0v-3.1A3.5 3.5 0 0 0 16 7a4 4 0 0 0-4-4Z" />
            <path d="M12 7v10" />
          </svg>
        </span>
        <span className="fv-reasoning-label">{label}</span>
        {hasContent && (
          <span className="fv-reasoning-chevron" aria-hidden="true">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        )}
      </button>

      {expanded && hasContent && (
        <pre id={paneId} className="fv-reasoning-pane">
          {content}
        </pre>
      )}
    </div>
  );
}
